import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, Loader2, Leaf, Gem, Bug, User, Beaker, Zap, Pill, Sparkles, ChevronRight, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINGDOM_ICONS = {
  plant: Leaf,
  mineral: Gem,
  animal: Bug,
  human_sarcode: User,
  nosode: Beaker,
  imponderabilia: Zap,
  synthetic: Pill,
};

const KINGDOM_ACCENTS = {
  plant: "#7B906F",
  mineral: "#A8896C",
  animal: "#C15C3D",
  human_sarcode: "#8B6C9E",
  nosode: "#6B7A9E",
  imponderabilia: "#D4AF37",
  synthetic: "#B87A5A",
};

export default function KingdomsPanel({ profileId }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKingdom, setSelectedKingdom] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [horoscopeMatch, setHoroscopeMatch] = useState(null);
  const [matching, setMatching] = useState(false);
  const [remedyOpen, setRemedyOpen] = useState(false);
  const [remedyData, setRemedyData] = useState(null);
  const [remedyLoading, setRemedyLoading] = useState(false);

  const openRemedy = useCallback(async (name) => {
    if (!name) return;
    setRemedyOpen(true);
    setRemedyLoading(true);
    setRemedyData(null);
    try {
      const url = `${API}/kingdoms/remedy/${encodeURIComponent(name)}` +
                  (profileId ? `?profile_id=${profileId}` : "");
      const { data } = await axios.get(url);
      setRemedyData(data);
    } catch (e) { toast.error("Failed to load remedy keynotes"); setRemedyOpen(false); }
    setRemedyLoading(false);
  }, [profileId]);

  // load tree once
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/kingdoms/tree`);
        setTree(data);
      } catch (e) {
        toast.error("Failed to load kingdoms");
      }
      setLoading(false);
    })();
  }, []);

  // debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/kingdoms/search`, { params: { q: query } });
        setSearchResults(data.matches || []);
      } catch { /* silent */ }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/kingdoms/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "homoeopathic-kingdoms-study-guide.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Study guide downloaded");
    } catch { toast.error("PDF download failed"); }
    setDownloading(false);
  };

  const matchFromHoroscope = useCallback(async () => {
    if (!profileId) { toast.error("Pick a profile first"); return; }
    setMatching(true);
    try {
      const { data } = await axios.get(`${API}/kingdoms/match-from-horoscope/${profileId}`);
      setHoroscopeMatch(data);
      if ((data.matched_kingdoms || []).length === 0 && (data.remedies_mentioned || []).length === 0) {
        toast.info("No matches — open the Homeopathy, Miasm & Kentian tabs first to build the horoscope narrative cache.");
      } else {
        toast.success(`${data.matched_kingdoms.length} kingdom matches from horoscope`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match failed");
    }
    setMatching(false);
  }, [profileId]);

  const jumpToFamily = (kingdom_key, family_key) => {
    if (!tree) return;
    const k = tree.kingdoms.find((x) => x.key === kingdom_key);
    if (!k) return;
    setSelectedKingdom(k);
    const f = k.families.find((x) => x.key === family_key);
    setSelectedFamily(f || null);
    setSearchResults(null); setQuery("");
    setTimeout(() => {
      document.getElementById("kingdom-family-view")?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  const activeKingdom = selectedKingdom || (tree?.kingdoms?.[0]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Loading kingdoms…</div>;
  if (!tree) return null;

  return (
    <div className="space-y-6" data-testid="kingdoms-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">Materia Medica</div>
          <div className="font-heading text-xl text-[#E6E1D6]">Homoeopathic Kingdoms — Study Hub</div>
          <div className="text-xs text-[#A19E95] mt-1">
            {tree.kingdoms.length} kingdoms · {tree.kingdoms.reduce((n, k) => n + k.families.length, 0)} families ·{" "}
            {tree.kingdoms.reduce((n, k) => n + k.families.reduce((m, f) => m + f.remedies.length, 0), 0)} remedy examples · zero AI credits per browse
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {profileId && (
            <Button onClick={matchFromHoroscope} disabled={matching}
                    variant="outline"
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    data-testid="kingdoms-horoscope-btn">
              {matching ? <><Loader2 size={14} className="mr-2 animate-spin" /> Matching…</>
                        : <><Sparkles size={14} className="mr-2" /> Match from horoscope</>}
            </Button>
          )}
          <Button onClick={downloadPdf} disabled={downloading}
                  variant="outline"
                  className="border-[#2A2D35] text-[#E6E1D6] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                  data-testid="kingdoms-pdf-btn">
            {downloading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Building PDF…</>
                         : <><Download size={14} className="mr-2" /> Study guide PDF</>}
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6B64]" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a remedy, family or theme (e.g. Lachesis, Snakes, envy, restlessness…)"
          className="pl-9 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
          data-testid="kingdoms-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setSearchResults(null); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6B64] hover:text-[#E6E1D6]">
            <X size={14} />
          </button>
        )}
      </div>

      {searchResults && searchResults.length > 0 && (
        <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-3 space-y-1 max-h-64 overflow-auto"
             data-testid="kingdoms-search-results">
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64] mb-1">
            {searchResults.length} matches
          </div>
          {searchResults.map((m, i) => (
            <button key={i} onClick={() => jumpToFamily(m.kingdom_key, m.family_key)}
                    className="w-full text-left px-3 py-2 rounded-sm hover:bg-[#0F1115] flex items-center gap-2 text-sm">
              <span className="text-[#D4AF37]">{m.remedy || m.family || m.kingdom}</span>
              <span className="text-[#6E6B64] text-xs">→ {m.kingdom}{m.family ? " · " + m.family : ""}</span>
            </button>
          ))}
        </div>
      )}
      {searching && <div className="text-xs text-[#6E6B64] font-mono">Searching…</div>}

      {/* Horoscope matches */}
      {horoscopeMatch && (
        <div className="border border-[#D4AF37]/40 bg-[#151821] rounded-sm p-4 space-y-3"
             data-testid="kingdoms-horoscope-match">
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37]">
            From horoscope · {horoscopeMatch.blobs_found} cached documents scanned · zero AI
          </div>
          <div className="text-sm text-[#E6E1D6]">
            Top-scoring kingdoms & families matching{" "}
            <span className="text-[#D4AF37]">{horoscopeMatch.profile_name || "this profile"}</span>'s
            existing horoscope narrative:
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {horoscopeMatch.matched_kingdoms.map((m, i) => (
              <button key={i}
                      onClick={() => jumpToFamily(m.kingdom_key, m.family_key)}
                      className="text-left border border-[#2A2D35] bg-[#0F1115] hover:border-[#D4AF37]/40 rounded-sm px-3 py-2">
                <div className="text-sm text-[#E6E1D6] font-medium">
                  {m.family || m.kingdom}
                  <span className="ml-2 text-[10px] font-mono text-[#D4AF37]">score {m.score}</span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6E6B64]">
                  {m.kingdom} · {m.keyword_hits.slice(0, 4).join(", ")}
                </div>
                {m.theme && <div className="text-xs text-[#A19E95] mt-1">{m.theme}</div>}
              </button>
            ))}
          </div>
          {horoscopeMatch.remedies_mentioned.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64] mt-2 mb-1">
                Remedies explicitly named in earlier horoscope analyses ({horoscopeMatch.remedies_mentioned.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {horoscopeMatch.remedies_mentioned.map((r, i) => (
                  <button key={i}
                          onClick={() => openRemedy(r.remedy)}
                          className="text-[11px] px-2 py-0.5 rounded-sm border border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                          data-testid={`remedy-mention-${r.remedy.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`}>
                    {r.remedy}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Kingdom rail */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="kingdoms-rail">
        {tree.kingdoms.map((k) => {
          const Icon = KINGDOM_ICONS[k.key] || Leaf;
          const active = activeKingdom?.key === k.key;
          const accent = KINGDOM_ACCENTS[k.key] || "#D4AF37";
          return (
            <button key={k.key}
                    onClick={() => { setSelectedKingdom(k); setSelectedFamily(null); }}
                    style={active ? { borderColor: accent, background: accent + "22" } : {}}
                    className={`text-left border rounded-sm px-3 py-3 transition-colors
                      ${active ? "text-[#E6E1D6]"
                              : "border-[#2A2D35] bg-[#151821] text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#E6E1D6]"}`}
                    data-testid={`kingdom-tab-${k.key}`}>
              <Icon size={16} style={{ color: accent }} />
              <div className="text-sm font-heading mt-1">{k.name}</div>
              <div className="text-[9px] uppercase tracking-widest font-mono text-[#6E6B64] mt-1">
                {k.families.length} families
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected kingdom detail */}
      {activeKingdom && (
        <div id="kingdom-family-view"
             className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-4"
             data-testid={`kingdom-detail-${activeKingdom.key}`}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">
              {activeKingdom.name} · Core theme
            </div>
            <div className="text-lg font-heading text-[#D4AF37]">{activeKingdom.core_theme}</div>
            <div className="text-sm text-[#A19E95] mt-1 leading-relaxed">{activeKingdom.description}</div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            {/* Family list */}
            <div className="md:col-span-1 space-y-1 border-r border-[#2A2D35] pr-3">
              <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64] mb-1">
                Families ({activeKingdom.families.length})
              </div>
              {activeKingdom.families.map((f) => {
                const active = selectedFamily?.key === f.key;
                return (
                  <button key={f.key}
                          onClick={() => setSelectedFamily(f)}
                          className={`w-full text-left px-2 py-2 rounded-sm text-sm flex items-center gap-2
                            ${active ? "bg-[#D4AF37]/10 text-[#E6E1D6]"
                                     : "text-[#A19E95] hover:bg-[#0F1115] hover:text-[#E6E1D6]"}`}
                          data-testid={`kingdom-family-${activeKingdom.key}-${f.key}`}>
                    <ChevronRight size={12} className={active ? "text-[#D4AF37]" : "text-[#6E6B64]"} />
                    <span className="truncate">{f.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Family detail */}
            <div className="md:col-span-2 space-y-3">
              {selectedFamily ? (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">Family</div>
                    <div className="text-lg font-heading text-[#E6E1D6]">{selectedFamily.name}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">Archetypal mental theme</div>
                    <div className="text-sm text-[#E6E1D6] leading-relaxed">{selectedFamily.theme}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64] mb-1">
                      Example remedies ({selectedFamily.remedies.length})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedFamily.remedies.map((r) => (
                        <button key={r}
                              onClick={() => openRemedy(r)}
                              className="text-xs px-2 py-1 rounded-sm border border-[#2A2D35] bg-[#0F1115] text-[#A19E95] hover:text-[#D4AF37] hover:border-[#D4AF37]/40 flex items-center gap-1"
                              data-testid={`remedy-chip-${r.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`}>
                          <BookOpen size={10} className="opacity-60" />
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-[#6E6B64] italic">
                  Pick a family on the left to see its archetypal mental theme and example remedies.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remedy Deep-Dive modal */}
      <Dialog open={remedyOpen} onOpenChange={setRemedyOpen}>
        <DialogContent className="bg-[#151821] border border-[#2A2D35] text-[#E6E1D6] max-w-3xl max-h-[85vh] overflow-y-auto"
                       data-testid="remedy-modal">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] font-heading text-xl flex items-center gap-2">
              <BookOpen size={18} /> {remedyData?.remedy || "Loading…"}
            </DialogTitle>
          </DialogHeader>
          {remedyLoading && <div className="text-[#A19E95] text-sm py-4">Loading keynotes…</div>}
          {remedyData && (
            <div className="space-y-4 text-sm" data-testid="remedy-modal-content">
              {remedyData.placement && (
                <div className="border border-[#2A2D35] bg-[#0F1115] rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">
                    Kingdom placement
                  </div>
                  <div className="text-sm text-[#E6E1D6]">
                    {remedyData.placement.kingdom} → <span className="text-[#D4AF37]">{remedyData.placement.family}</span>
                  </div>
                  <div className="text-xs text-[#A19E95] mt-1 italic">{remedyData.placement.family_theme}</div>
                </div>
              )}

              {!remedyData.found_keynotes && (
                <div className="border border-[#2A2D35] bg-[#0F1115] rounded-sm p-3 text-xs text-[#A19E95] italic">
                  {remedyData.keynotes.note}
                </div>
              )}

              {remedyData.found_keynotes && (
                <>
                  {[
                    ["Boger keynotes", remedyData.keynotes.boger, "boger"],
                    ["Kent keynotes", remedyData.keynotes.kent, "kent"],
                    ["Mind", remedyData.keynotes.mind, "mind"],
                    ["Generalities", remedyData.keynotes.generalities, "generalities"],
                  ].map(([label, arr, key]) => (arr && arr.length > 0) && (
                    <div key={key}>
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37] mb-1">
                        {label}
                      </div>
                      <ul className="space-y-1 text-[#E6E1D6]">
                        {arr.map((line, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#6E6B64] shrink-0">◈</span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(remedyData.keynotes.better && remedyData.keynotes.better.length > 0) && (
                      <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-emerald-300 mb-1">
                          Better from
                        </div>
                        <div className="text-xs text-[#E6E1D6] leading-relaxed">
                          {remedyData.keynotes.better.join(" · ")}
                        </div>
                      </div>
                    )}
                    {(remedyData.keynotes.worse && remedyData.keynotes.worse.length > 0) && (
                      <div className="border border-rose-500/30 bg-rose-500/5 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-rose-300 mb-1">
                          Worse from
                        </div>
                        <div className="text-xs text-[#E6E1D6] leading-relaxed">
                          {remedyData.keynotes.worse.join(" · ")}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {remedyData.horoscope_mentions && remedyData.horoscope_mentions.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37] mb-1">
                    Mentioned in this profile's cached horoscope ({remedyData.horoscope_mentions.length})
                  </div>
                  <div className="space-y-1">
                    {remedyData.horoscope_mentions.map((m, i) => (
                      <div key={i} className="border-l-2 border-[#D4AF37]/40 pl-2 text-xs">
                        <span className="text-[10px] font-mono text-[#6E6B64] uppercase tracking-widest">
                          {m.source.replace("_cache", "").replace("_", " ")}
                        </span>
                        <div className="text-[#A19E95] italic">{m.snippet}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
